import {
  Autocomplete,
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { Aircraft } from "../../types";
import { defaultTo } from "../../utils/lodash-replacements";

const SmallTextArea = ({ isBeingSelected = false, ...props }) => {
  const extraProps = { ...props };
  if (!props.value && props.value !== 0 && props.required) {
    extraProps.color = "error";
    extraProps.focused = true;
    extraProps.required = null;
  }
  if (isBeingSelected) {
    extraProps.color = "success";
    extraProps.focused = true;
  }
  return (
    <TextField
      size="small"
      {...extraProps}
      sx={{ ...(props.sx || {}) }}
      InputLabelProps={{
        sx: {
          fontSize: 12,
        },
      }}
      inputProps={{
        sx: {
          fontSize: 12,
        },
        ...props.inputProps,
      }}
    />
  );
};

type Props = {
  aircrafts: Aircraft[];
  aircraft: Aircraft;
  setAircraft: (a: Aircraft) => void;
  setAircraftValue: (key: string, value: any) => void;
  // handlers
  onClickAutoOutline: () => void;
  onClickNose: () => void;
  contourNumber: number;
  setContourNumber: (x: number) => void;
  select: boolean;
  setSelect: (x: boolean) => void;
  setShapes: (shapes: number[][][]) => void;
  geomBeingSelected: string;
  setGeomBeingSelected: (x: string) => void;
  taggableArea: string;
  onChangeTaggableArea: (x: string) => void;
  onClickCalculateFromReferencePoints: () => void;
};

export const LabelingInputForm: React.FC<Props> = (props) => {
  const {
    aircrafts,
    aircraft,
    setAircraft,
    setAircraftValue,
    onClickAutoOutline,
    contourNumber,
    setContourNumber,
    setShapes,
    setGeomBeingSelected,
    select,
    setSelect,
    taggableArea,
    onChangeTaggableArea,
  } = props;

  const { t } = useTranslation();
  const [linkedAircraft, setLinkedAircraft] = React.useState<Aircraft>();
  const [noPropeller, setNoPropeller] = React.useState<boolean>(false);

  const needsPropellerSelection =
    !Boolean(aircraft.propeller_geom) && !noPropeller;

  React.useEffect(() => {
    setLinkedAircraft(aircrafts.find((a) => a.id === aircraft.linked_to));
  }, [aircraft.linked_to]);

  return (
    <Stack sx={{ mt: 1 }}>
      <Stack>
        <SmallTextArea disabled label={t('screens.labelingTool.inputForm.labels.id')} value={defaultTo(aircraft.id, "")} />
        <br />
        <SmallTextArea
          required
          label={t('screens.labelingTool.inputForm.labels.make')}
          onChange={(evt: React.ChangeEvent<HTMLInputElement>) =>
            setAircraftValue("make", evt.target.value)
          }
          value={defaultTo(aircraft.make, "")}
        />
        <br />
        <SmallTextArea
          required
          label={t('screens.labelingTool.inputForm.labels.model')}
          onChange={(evt: React.ChangeEvent<HTMLInputElement>) =>
            setAircraftValue("model", evt.target.value)
          }
          value={defaultTo(aircraft.model, "")}
        />
        <br />
        <Stack direction="row">
          <Autocomplete
            disablePortal
            sx={{ width: 500 }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('screens.labelingTool.inputForm.labels.linkAircraftTo')}
                size="small"
              />
            )}
            getOptionLabel={(option: Aircraft) =>
              `${option.make} ${option.model}`
            }
            options={aircrafts}
            onChange={(event: any, newValue: Aircraft | null) => {
              setLinkedAircraft(newValue);
            }}
            value={linkedAircraft || null}
          />

          <Button
            disabled={!Boolean(aircraft.linked_to)}
            size="small"
            onClick={() => {
              setAircraft({ ...aircraft, linked_to: null });
              setLinkedAircraft(null);
            }}
          >
            {t('screens.labelingTool.inputForm.buttons.remove')}
          </Button>
          <Button
            disabled={!Boolean(linkedAircraft)}
            size="small"
            onClick={() => {
              setAircraft({ ...aircraft, linked_to: linkedAircraft.id });
            }}
          >
            {t('screens.labelingTool.inputForm.buttons.save')}
          </Button>
        </Stack>
      </Stack>
      {Boolean(aircraft.linked_to) ? (
        <div />
      ) : (
        <>
          <Typography variant="subtitle1">{t('screens.labelingTool.inputForm.sections.referencePoints')}</Typography>
          <Stack>
            <Stack direction="row" spacing={2}>
              <SmallTextArea
                isBeingSelected={taggableArea === "reference_point_wing_bottom"}
                required
                label={t('screens.labelingTool.inputForm.labels.wingBottom')}
                inputProps={{
                  onFocus: () => {
                    onChangeTaggableArea("reference_point_wing_bottom");
                  },
                }}
                onChange={(evt: React.ChangeEvent<HTMLInputElement>) => {
                  setAircraftValue(
                    "reference_point_wing_bottom",
                    evt.target.value
                  );
                }}
                value={defaultTo(aircraft.reference_point_wing_bottom, 0)}
              />
              <SmallTextArea
                isBeingSelected={taggableArea === "reference_point_wing_top"}
                required
                label={t('screens.labelingTool.inputForm.labels.wingTop')}
                inputProps={{
                  onFocus: () => {
                    onChangeTaggableArea("reference_point_wing_top");
                  },
                }}
                onChange={(evt: React.ChangeEvent<HTMLInputElement>) =>
                  setAircraftValue("reference_point_wing_top", evt.target.value)
                }
                value={defaultTo(aircraft.reference_point_wing_top, 0)}
              />
            </Stack>
            <br />
            <Stack direction="row" spacing={2}>
              <SmallTextArea
                isBeingSelected={taggableArea === "reference_point_tail_bottom"}
                required
                label={t('screens.labelingTool.inputForm.labels.horizontalTailBottom')}
                inputProps={{
                  onFocus: () => {
                    onChangeTaggableArea("reference_point_tail_bottom");
                  },
                }}
                onChange={(evt: React.ChangeEvent<HTMLInputElement>) => {
                  setAircraftValue(
                    "reference_point_tail_bottom",
                    evt.target.value
                  );
                }}
                value={defaultTo(aircraft.reference_point_tail_bottom, 0)}
              />
              <SmallTextArea
                isBeingSelected={taggableArea === "reference_point_tail_top"}
                required
                label={t('screens.labelingTool.inputForm.labels.horizontalTailTop')}
                inputProps={{
                  onFocus: () => {
                    onChangeTaggableArea("reference_point_tail_top");
                  },
                }}
                onChange={(evt: React.ChangeEvent<HTMLInputElement>) => {
                  setAircraftValue(
                    "reference_point_tail_top",
                    evt.target.value
                  );
                }}
                value={defaultTo(aircraft.reference_point_tail_top, 0)}
              />
            </Stack>
            <br />
            <Stack direction="row" spacing={2}>
              <SmallTextArea
                isBeingSelected={
                  taggableArea === "reference_point_vertical_tail_fin_bottom"
                }
                required
                type="number"
                label={t('screens.labelingTool.inputForm.labels.verticalTailBottom')}
                inputProps={{
                  onFocus: () => {
                    onChangeTaggableArea(
                      "reference_point_vertical_tail_fin_bottom"
                    );
                  },
                }}
                onChange={(evt: React.ChangeEvent<HTMLInputElement>) => {
                  setAircraftValue(
                    "reference_point_vertical_tail_fin_bottom",
                    evt.target.value
                  );
                }}
                value={defaultTo(
                  aircraft.reference_point_vertical_tail_fin_bottom,
                  0
                )}
              />
              <SmallTextArea
                isBeingSelected={
                  taggableArea === "reference_point_vertical_tail_fin_top"
                }
                required
                label={t('screens.labelingTool.inputForm.labels.verticalTailTop')}
                inputProps={{
                  onFocus: () => {
                    onChangeTaggableArea(
                      "reference_point_vertical_tail_fin_top"
                    );
                  },
                }}
                onChange={(evt: React.ChangeEvent<HTMLInputElement>) => {
                  setAircraftValue(
                    "reference_point_vertical_tail_fin_top",
                    evt.target.value
                  );
                }}
                value={defaultTo(
                  aircraft.reference_point_vertical_tail_fin_top,
                  0
                )}
              />
            </Stack>
            <br />
            <Stack direction="row" spacing={2}>
              <SmallTextArea
                isBeingSelected={
                  taggableArea === "reference_point_cabin_bottom"
                }
                required
                type="number"
                label={t('screens.labelingTool.inputForm.labels.cabinBottom')}
                inputProps={{
                  onFocus: () => {
                    onChangeTaggableArea("reference_point_cabin_bottom");
                  },
                }}
                onChange={(evt: React.ChangeEvent<HTMLInputElement>) => {
                  setAircraftValue(
                    "reference_point_cabin_bottom",
                    evt.target.value
                  );
                }}
                value={defaultTo(aircraft.reference_point_cabin_bottom, 0)}
              />
              <SmallTextArea
                isBeingSelected={taggableArea === "reference_point_cabin_top"}
                required
                label={t('screens.labelingTool.inputForm.labels.cabinTop')}
                inputProps={{
                  onFocus: () => {
                    onChangeTaggableArea("reference_point_cabin_top");
                  },
                }}
                onChange={(evt: React.ChangeEvent<HTMLInputElement>) => {
                  setAircraftValue(
                    "reference_point_cabin_top",
                    evt.target.value
                  );
                }}
                value={defaultTo(aircraft.reference_point_cabin_top, 0)}
              />
            </Stack>
            <hr />
            <Typography variant="subtitle1">{t('screens.labelingTool.inputForm.sections.orientation')}</Typography>
            <Stack>
              <Stack direction="row" alignItems="center">
                <SmallTextArea
                  required
                  fullWidth
                  label={t('screens.labelingTool.inputForm.labels.nose')}
                  inputProps={{
                    onFocus: () => {
                      setGeomBeingSelected("nose");
                    },
                  }}
                  onChange={(evt: React.ChangeEvent<HTMLInputElement>) =>
                    setAircraftValue("nose", evt.target.value)
                  }
                  value={defaultTo(
                    JSON.stringify(aircraft.nose?.coordinates),
                    ""
                  )}
                />
                <SmallTextArea
                  required
                  fullWidth
                  type="number"
                  inputProps={{
                    onFocus: () => {
                      setGeomBeingSelected("midline");
                    },
                  }}
                  label={t('screens.labelingTool.inputForm.labels.midline')}
                  onChange={(evt: React.ChangeEvent<HTMLInputElement>) =>
                    setAircraftValue("midline", evt.target.value)
                  }
                  value={defaultTo(aircraft.midline, 0)}
                />
              </Stack>
            </Stack>
          </Stack>
          <Stack sx={{ mt: 1 }}>
            <Typography variant="subtitle1">{t('screens.labelingTool.inputForm.sections.propellers')}</Typography>
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant={noPropeller ? "contained" : "outlined"}
                color={needsPropellerSelection ? "error" : "primary"}
                onClick={() => {
                  // something?
                  setNoPropeller(!noPropeller);
                }}
              >
                {t('screens.labelingTool.inputForm.buttons.noPropeller')}
              </Button>
              <Button
                size="small"
                variant="outlined"
                color={needsPropellerSelection ? "error" : "primary"}
                onClick={() => {
                  setShapes([]);
                  setGeomBeingSelected("propeller_geom");
                  setNoPropeller(false);
                  setSelect(!select);
                }}
              >
                {t('screens.labelingTool.inputForm.buttons.select')}
              </Button>
              <Button
                size="small"
                color="warning"
                disabled={!Boolean(aircraft.propeller_geom)}
                onClick={() => {
                  setAircraftValue("propeller_geom", null);
                }}
              >
                {t('screens.labelingTool.inputForm.buttons.clear')}
              </Button>
              <Checkbox checked={Boolean(aircraft.propeller_geom)} />
            </Stack>
          </Stack>
          <Stack sx={{ mt: 1 }}>
            <Typography variant="subtitle1">{t('screens.labelingTool.inputForm.sections.dimensions')}</Typography>
            <Stack direction="row">
              <SmallTextArea
                required
                type="number"
                label={t('screens.labelingTool.inputForm.labels.length')}
                onChange={(evt: React.ChangeEvent<HTMLInputElement>) =>
                  setAircraftValue("length", evt.target.value)
                }
                value={defaultTo(aircraft.length, 0)}
              />
              <SmallTextArea
                required
                type="number"
                label={t('screens.labelingTool.inputForm.labels.wingspan')}
                onChange={(evt: React.ChangeEvent<HTMLInputElement>) =>
                  setAircraftValue("wingspan", evt.target.value)
                }
                value={defaultTo(aircraft.wingspan, 0)}
              />
              <SmallTextArea
                required
                type="number"
                label={t('screens.labelingTool.inputForm.labels.tailHeight')}
                onChange={(evt: React.ChangeEvent<HTMLInputElement>) =>
                  setAircraftValue("tail_height", evt.target.value)
                }
                value={defaultTo(aircraft.tail_height, 0)}
              />
            </Stack>
          </Stack>
          <Stack sx={{ mt: 1 }}>
            <Typography variant="subtitle1">{t('screens.labelingTool.inputForm.sections.outline')}</Typography>
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                onClick={onClickAutoOutline}
                variant="outlined"
                color={aircraft.geom ? "primary" : "error"}
              >
                {t('screens.labelingTool.inputForm.buttons.auto')}
              </Button>
              <SmallTextArea
                type="number"
                label={t('screens.labelingTool.inputForm.labels.contourNumber')}
                onChange={(evt: React.ChangeEvent<HTMLInputElement>) =>
                  setContourNumber(Number(evt.target.value))
                }
                value={defaultTo(contourNumber, 1)}
              />
              <SmallTextArea
                label={t('screens.labelingTool.inputForm.labels.simplifyPercent')}
                type="number"
                inputProps={{
                  step: 0.005,
                  min: 0,
                  max: 1,
                }}
                onChange={(evt: React.ChangeEvent<HTMLInputElement>) =>
                  setAircraftValue("simplify_pct", evt.target.value)
                }
                value={defaultTo(aircraft.simplify_pct, 0)}
              />
            </Stack>
          </Stack>
          <Stack sx={{ mt: 1 }}>
            <Typography variant="subtitle1">{t('screens.labelingTool.inputForm.sections.overlaps')}</Typography>
            <Typography variant="subtitle2">{t('screens.labelingTool.inputForm.sections.wings')}</Typography>
            <Stack direction="row">
              <Button
                size="small"
                onClick={() => {
                  setShapes([]);
                  setGeomBeingSelected("wing_geom");
                  setSelect(!select);
                }}
              >
                {t('screens.labelingTool.inputForm.buttons.select')}
              </Button>
              <Button
                size="small"
                color="warning"
                disabled={!Boolean(aircraft.wing_geom)}
                onClick={() => setAircraftValue("wing_geom", null)}
              >
                {t('screens.labelingTool.inputForm.buttons.clear')}
              </Button>
              <Checkbox checked={Boolean(aircraft.wing_geom)} />
              <SmallTextArea
                required
                label={t('screens.labelingTool.inputForm.labels.bottom')}
                onChange={(evt: React.ChangeEvent<HTMLInputElement>) =>
                  setAircraftValue("wing_bottom", evt.target.value)
                }
                value={defaultTo(aircraft.wing_bottom, 0)}
              />
              <SmallTextArea
                required
                label={t('screens.labelingTool.inputForm.labels.top')}
                onChange={(evt: React.ChangeEvent<HTMLInputElement>) =>
                  setAircraftValue("wing_top", evt.target.value)
                }
                value={defaultTo(aircraft.wing_top, 0)}
              />
            </Stack>
            <Typography variant="subtitle2">{t('screens.labelingTool.inputForm.sections.horizontalTail')}</Typography>
            <Stack direction="row">
              <Button
                size="small"
                onClick={() => {
                  setShapes([]);
                  setGeomBeingSelected("tail_geom");
                  setSelect(!select);
                }}
              >
                {t('screens.labelingTool.inputForm.buttons.select')}
              </Button>
              <Button
                size="small"
                color="warning"
                disabled={!Boolean(aircraft.tail_geom)}
                onClick={() => setAircraftValue("tail_geom", null)}
              >
                {t('screens.labelingTool.inputForm.buttons.clear')}
              </Button>
              <Checkbox checked={Boolean(aircraft.tail_geom)} />
              <SmallTextArea
                required
                label={t('screens.labelingTool.inputForm.labels.bottom')}
                onChange={(evt: React.ChangeEvent<HTMLInputElement>) =>
                  setAircraftValue("tail_bottom", evt.target.value)
                }
                value={defaultTo(aircraft.tail_bottom, 0)}
              />
              <SmallTextArea
                required
                label={t('screens.labelingTool.inputForm.labels.top')}
                onChange={(evt: React.ChangeEvent<HTMLInputElement>) =>
                  setAircraftValue("tail_top", evt.target.value)
                }
                value={defaultTo(aircraft.tail_top, 0)}
                // helperText={
                //   aircraft.tail_top > aircraft.vertical_tail_fin_bottom
                //     ? "Tail top must be above vertical tail fin bottom"
                //     : ""
                // }
                // error={aircraft.tail_top > aircraft.vertical_tail_fin_bottom}
              />
            </Stack>
            <Typography variant="subtitle2">{t('screens.labelingTool.inputForm.sections.verticalTailFin')}</Typography>
            <Stack direction="row">
              <Button
                size="small"
                onClick={() => {
                  setShapes([]);
                  setGeomBeingSelected("vertical_tail_fin_geom");
                  setSelect(!select);
                }}
              >
                {t('screens.labelingTool.inputForm.buttons.select')}
              </Button>
              <Button
                size="small"
                color="warning"
                disabled={!Boolean(aircraft.vertical_tail_fin_geom)}
                onClick={() => setAircraftValue("vertical_tail_fin_geom", null)}
              >
                {t('screens.labelingTool.inputForm.buttons.clear')}
              </Button>
              <Checkbox checked={Boolean(aircraft.vertical_tail_fin_geom)} />
              <SmallTextArea
                required
                label={t('screens.labelingTool.inputForm.labels.bottom')}
                onChange={(evt: React.ChangeEvent<HTMLInputElement>) =>
                  setAircraftValue("vertical_tail_fin_bottom", evt.target.value)
                }
                value={defaultTo(aircraft.vertical_tail_fin_bottom, 0)}
              />
              <SmallTextArea
                required
                label={t('screens.labelingTool.inputForm.labels.top')}
                onChange={(evt: React.ChangeEvent<HTMLInputElement>) =>
                  setAircraftValue("vertical_tail_fin_top", evt.target.value)
                }
                value={defaultTo(aircraft.vertical_tail_fin_top, 0)}
              />
            </Stack>
            <Typography variant="subtitle2">{t('screens.labelingTool.inputForm.sections.cabin')}</Typography>
            <Stack direction="row">
              <SmallTextArea
                required
                type="number"
                label={t('screens.labelingTool.inputForm.labels.bottom')}
                onChange={(evt: React.ChangeEvent<HTMLInputElement>) =>
                  setAircraftValue("cabin_bottom", evt.target.value)
                }
                value={defaultTo(aircraft.cabin_bottom, 0)}
              />
              <SmallTextArea
                required
                type="number"
                label={t('screens.labelingTool.inputForm.labels.top')}
                onChange={(evt: React.ChangeEvent<HTMLInputElement>) =>
                  setAircraftValue("cabin_top", evt.target.value)
                }
                value={defaultTo(aircraft.cabin_top, 0)}
              />
            </Stack>
          </Stack>
          <Typography variant="subtitle1">{t('screens.labelingTool.inputForm.sections.towing')}</Typography>
          <Stack direction="row">
            <SmallTextArea
              label={t('screens.labelingTool.inputForm.labels.limitDegrees')}
              onChange={(evt: React.ChangeEvent<HTMLInputElement>) =>
                setAircraftValue("wheel_rotation_limit", evt.target.value)
              }
              value={defaultTo(aircraft.wheel_rotation_limit, 0)}
            />
            <SmallTextArea
              required
              label={t('screens.labelingTool.inputForm.labels.front')}
              type="number"
              inputProps={{
                onFocus: () => {
                  setGeomBeingSelected("front_wheels");
                },
              }}
              onChange={(evt: React.ChangeEvent<HTMLInputElement>) =>
                setAircraftValue("front_wheels", evt.target.value)
              }
              value={defaultTo(aircraft.front_wheels, 0)}
            />
            <SmallTextArea
              required
              type="number"
              label={t('screens.labelingTool.inputForm.labels.rear')}
              inputProps={{
                onFocus: () => {
                  setGeomBeingSelected("rear_wheels");
                },
              }}
              onChange={(evt: React.ChangeEvent<HTMLInputElement>) =>
                setAircraftValue("rear_wheels", evt.target.value)
              }
              value={defaultTo(aircraft.rear_wheels, 0)}
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  onChange={(evt: React.ChangeEvent<HTMLInputElement>) =>
                    setAircraftValue("tail_wheel", evt.target.checked)
                  }
                  checked={Boolean(aircraft.tail_wheel)}
                />
              }
              label={t('screens.labelingTool.inputForm.labels.tailWheel')}
            />
          </Stack>
          <Stack sx={{ mt: 1 }}>
            <Typography variant="subtitle1">{t('screens.labelingTool.inputForm.sections.fuel')}</Typography>
            <Stack direction="row" spacing={1}>
              <SmallTextArea
                label={t('screens.labelingTool.inputForm.labels.fuelCapacity')}
                type="number"
                onChange={(evt: React.ChangeEvent<HTMLInputElement>) =>
                  setAircraftValue("fuel_capacity", evt.target.value)
                }
                value={defaultTo(aircraft.fuel_capacity, 0)}
              />
              <TextField
                size="small"
                fullWidth
                label={t('screens.labelingTool.inputForm.labels.fuelType')}
                value={aircraft.fuel_type ?? ""}
                select
                onChange={(evt: React.ChangeEvent<HTMLInputElement>) =>
                  setAircraftValue("fuel_type", evt.target.value)
                }
              >
                <MenuItem value="Jet A">{t('screens.labelingTool.inputForm.fuelTypes.jetA')}</MenuItem>
                <MenuItem value="AvGas">{t('screens.labelingTool.inputForm.fuelTypes.avGas')}</MenuItem>
                <MenuItem value="Other">{t('screens.labelingTool.inputForm.fuelTypes.other')}</MenuItem>
              </TextField>
            </Stack>
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              <TextField
                size="small"
                fullWidth
                label={t('screens.labelingTool.inputForm.labels.cruiseBurn')}
                type="number"
                onChange={(evt: React.ChangeEvent<HTMLInputElement>) =>
                  setAircraftValue("cruise_burn_gal_per_hour", evt.target.value)
                }
                value={defaultTo(aircraft.cruise_burn_gal_per_hour, 0)}
                helperText={t('screens.labelingTool.inputForm.helperText.cruiseBurn')}
              />
              <TextField
                size="small"
                fullWidth
                label={t('screens.labelingTool.inputForm.labels.cruiseSpeed')}
                type="number"
                onChange={(evt: React.ChangeEvent<HTMLInputElement>) =>
                  setAircraftValue("cruise_speed_kts", evt.target.value)
                }
                value={defaultTo(aircraft.cruise_speed_kts, 0)}
                helperText={t('screens.labelingTool.inputForm.helperText.cruiseSpeed')}
              />
            </Stack>
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              <TextField
                size="small"
                fullWidth
                label={t('screens.labelingTool.inputForm.labels.reserves')}
                type="number"
                onChange={(evt: React.ChangeEvent<HTMLInputElement>) =>
                  setAircraftValue("reserves_gal", evt.target.value)
                }
                value={defaultTo(aircraft.reserves_gal, 0)}
                helperText={t('screens.labelingTool.inputForm.helperText.reserves')}
              />
              <TextField
                size="small"
                fullWidth
                label={t('screens.labelingTool.inputForm.labels.cruiseAltitude')}
                type="number"
                onChange={(evt: React.ChangeEvent<HTMLInputElement>) =>
                  setAircraftValue(
                    "typical_cruise_altitude_ft",
                    evt.target.value
                  )
                }
                value={defaultTo(aircraft.typical_cruise_altitude_ft, 0)}
                helperText={t('screens.labelingTool.inputForm.helperText.cruiseAltitude')}
              />
            </Stack>
          </Stack>
          <Typography variant="subtitle1">{t('screens.labelingTool.inputForm.sections.helicopter')}</Typography>
          <Stack direction="row">
            <FormControlLabel
              control={
                <Checkbox
                  checked={aircraft.is_helicopter}
                  onChange={(evt: React.ChangeEvent<HTMLInputElement>) =>
                    setAircraftValue("is_helicopter", evt.target.checked)
                  }
                />
              }
              label={t('screens.labelingTool.inputForm.labels.isHelicopter')}
            />
          </Stack>
        </>
      )}
    </Stack>
  );
};
